/**
 * Orchestrates the "measures dropped to zero" preview-then-confirm
 * gate on the edit-mode save flow (Task #54). Mirror image of
 * ``editAssessmentWithTeams`` — a pure helper that the view delegates
 * to so the subtle preview rules can be exercised by unit tests
 * without React.
 *
 * Rules (mirroring the inline comment in ``AssessmentEditView``'s
 * ``onSave`` preview branch):
 *   - Preview is only called when the assessment is currently Saved
 *     *and* the admin has dropped its measures to zero. A Draft
 *     assessment can already have zero measures, so there's nothing
 *     to revert; and any non-empty measure set keeps the assessment
 *     Saved, so there's no destructive side-effect to warn about.
 *   - When preview is skipped, save proceeds immediately.
 *   - When preview comes back with ``would_revert_to_draft=false``
 *     (e.g. there were no teams assigned in the first place) save
 *     proceeds immediately without surfacing a callout.
 *   - When preview comes back with ``would_revert_to_draft=true`` the
 *     helper returns the team list so the caller can render the
 *     inline accent callout and gate the save behind a confirm step.
 *     The save is deferred — the caller is expected to call
 *     ``performSave`` again from its confirm handler.
 *   - When preview itself fails the helper returns the error message
 *     and does *not* call ``performSave``. The destructive PATCH must
 *     not run when we couldn't show the admin what would be lost.
 */
import type {
  AssessmentDetail,
  AssessmentInput,
  AssessmentTeamRef,
  MeasureInput,
} from "./api";

export type PreviewMeasuresImpactAndSaveDeps = {
  previewImpact: (
    measures: MeasureInput[],
  ) => Promise<{
    would_revert_to_draft: boolean;
    teams_to_unassign: AssessmentTeamRef[];
  }>;
  performSave: (payload: AssessmentInput) => Promise<void>;
};

export type PreviewMeasuresImpactAndSaveResult =
  | { kind: "saved"; previewCalled: boolean }
  | { kind: "needs_confirm"; teams: AssessmentTeamRef[] }
  | { kind: "preview_error"; error: string };

export async function previewMeasuresImpactAndSave(
  payload: AssessmentInput,
  assessmentStatus: AssessmentDetail["status"],
  deps: PreviewMeasuresImpactAndSaveDeps,
): Promise<PreviewMeasuresImpactAndSaveResult> {
  const needsPreview =
    assessmentStatus === "saved" && payload.measures.length === 0;
  if (!needsPreview) {
    await deps.performSave(payload);
    return { kind: "saved", previewCalled: false };
  }
  let preview: {
    would_revert_to_draft: boolean;
    teams_to_unassign: AssessmentTeamRef[];
  };
  try {
    preview = await deps.previewImpact(payload.measures);
  } catch (e) {
    return {
      kind: "preview_error",
      error: (e as { message?: string })?.message || "Failed to preview impact",
    };
  }
  if (preview.would_revert_to_draft) {
    return { kind: "needs_confirm", teams: preview.teams_to_unassign };
  }
  await deps.performSave(payload);
  return { kind: "saved", previewCalled: true };
}
